/**
 * Breadcrumbs Component - Navigation path display for drill-down views
 *
 * Features:
 * - Clickable breadcrumb items to navigate to any level
 * - Visual separator (chevron icons)
 * - Current item highlighted without link
 * - Responsive design with overflow handling
 * - Keyboard accessible
 */

import React, { Fragment } from 'react';
import { ChevronRight, Home } from 'lucide-react';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface BreadcrumbItem {
  id?: string;
  label: string;
  level?: string;
  onClick?: () => void;
}

interface BreadcrumbsProps {
  crumbs: BreadcrumbItem[];
  onCrumbClick?: (index: number) => void;
  showHomeIcon?: boolean;
  maxItems?: number;
  className?: string;
  style?: React.CSSProperties;
}

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    padding: '0.5rem 0',
    flexWrap: 'wrap' as const,
    minHeight: '2rem',
  },
  crumbButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'var(--color-primary, #3b82f6)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
  },
  crumbButtonHover: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    textDecoration: 'underline',
  },
  currentCrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.25rem 0.5rem',
    color: 'var(--color-text, #374151)',
    fontSize: '0.875rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  separator: {
    color: 'var(--color-text-muted, #9ca3af)',
    flexShrink: 0,
  },
  homeIcon: {
    marginRight: '0.125rem',
  },
  truncated: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.25rem 0.5rem',
    color: 'var(--color-text-muted, #9ca3af)',
    cursor: 'default',
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  crumbs,
  onCrumbClick,
  showHomeIcon = true,
  maxItems = 5,
  className = '',
  style = {},
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  // Handle truncation for long breadcrumb trails
  const displayCrumbs = React.useMemo(() => {
    if (crumbs.length <= maxItems) {
      return crumbs;
    }

    // Keep first, last, and truncate middle
    const first = crumbs[0];
    const last = crumbs.slice(-2);
    return [first, { label: '...', isTruncated: true } as any, ...last];
  }, [crumbs, maxItems]);

  const handleClick = (index: number, crumb: BreadcrumbItem) => {
    // Don't navigate for current item or truncated marker
    if (index === crumbs.length - 1 || (crumb as any).isTruncated) {
      return;
    }

    if (crumb.onClick) {
      crumb.onClick();
    } else if (onCrumbClick) {
      // Calculate actual index if truncated
      let actualIndex = index;
      if (displayCrumbs.length < crumbs.length && index > 0) {
        if (index === 1) return; // Truncated marker
        actualIndex = crumbs.length - (displayCrumbs.length - index);
      }
      onCrumbClick(actualIndex);
    }
  };

  if (!crumbs || crumbs.length === 0) {
    return null;
  }

  return (
    <nav
      className={`breadcrumbs ${className}`}
      style={{ ...styles.container, ...style }}
      aria-label="Breadcrumb navigation"
    >
      {displayCrumbs.map((crumb, index) => {
        const isLast = index === displayCrumbs.length - 1;
        const isFirst = index === 0;
        const isTruncated = (crumb as any).isTruncated;
        const isHovered = hoveredIndex === index;
        const isClickable = !isLast && !isTruncated;

        return (
          <Fragment key={crumb.id || index}>
            {index > 0 && (
              <ChevronRight
                size={14}
                style={styles.separator}
                aria-hidden="true"
              />
            )}

            {isTruncated ? (
              <span style={styles.truncated} title="Click adjacent items to navigate">
                ...
              </span>
            ) : isLast ? (
              <span style={styles.currentCrumb} aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <button
                onClick={() => handleClick(index, crumb)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onFocus={() => setHoveredIndex(index)}
                onBlur={() => setHoveredIndex(null)}
                style={{
                  ...styles.crumbButton,
                  ...(isHovered ? styles.crumbButtonHover : {}),
                }}
                aria-label={`Navigate to ${crumb.label}`}
                tabIndex={0}
              >
                {isFirst && showHomeIcon && (
                  <Home size={14} style={styles.homeIcon} aria-hidden="true" />
                )}
                {crumb.label}
              </button>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
};

// ============================================================================
// COMPACT VERSION FOR TIGHT SPACES
// ============================================================================

interface CompactBreadcrumbsProps {
  crumbs: BreadcrumbItem[];
  onBack?: () => void;
  onReset?: () => void;
}

export const CompactBreadcrumbs: React.FC<CompactBreadcrumbsProps> = ({
  crumbs,
  onBack,
  onReset,
}) => {
  if (!crumbs || crumbs.length === 0) return null;

  const currentLabel = crumbs[crumbs.length - 1]?.label || 'View';
  const pathLabel = crumbs.map(c => c.label).join(' > ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '0.8125rem',
      }}
    >
      {crumbs.length > 1 && onBack && (
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.375rem 0.625rem',
            backgroundColor: 'var(--color-background-alt, #f3f4f6)',
            border: 'none',
            borderRadius: '4px',
            color: 'var(--color-text, #374151)',
            cursor: 'pointer',
            fontSize: '0.8125rem',
            transition: 'all 0.2s ease',
          }}
          title="Go back (Backspace)"
        >
          <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
          Back
        </button>
      )}

      <span
        style={{
          color: 'var(--color-text-muted, #6b7280)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '400px',
        }}
        title={pathLabel}
      >
        {pathLabel}
      </span>

      {crumbs.length > 1 && onReset && (
        <button
          onClick={onReset}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.375rem 0.625rem',
            backgroundColor: 'transparent',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: '4px',
            color: 'var(--color-text-muted, #6b7280)',
            cursor: 'pointer',
            fontSize: '0.8125rem',
            transition: 'all 0.2s ease',
          }}
          title="Reset to overview"
        >
          <Home size={12} />
          Reset
        </button>
      )}
    </div>
  );
};

export default Breadcrumbs;
